"use client";
import { GiftCardCartItem } from "@/app/(shop)/giftcard/components/giftCardCartItem";
import { GiftCardCartOrdersBanner } from "@/app/(shop)/giftcard/components/giftCardCartOrdersBanner";
import { useCurrency } from "@/app/providers/currencyContext";
import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";
import { DeliveryEstimateBanner } from "@/components/deliveryEstimateBanner/deliveryEstimateBanner";
import { OrderType, PickupTimeType, ServiceType } from "@/constants/enum";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import { Address } from "@/dto/address";
import { GiftCard } from "@/dto/giftCard";
import { OrderEstimates } from "@/dto/orderEstimates";
import {
  ScheduleDateType,
  ScheduledTime,
  ScheduleTimeType,
} from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import { Cart } from "@/models/cart/cart";
import { RetailCart } from "@/models/cart/retailCart";
import { RetailCartItem } from "@/models/cart/types";
import { TFunction } from "@/types";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import pom from "@/utils/pom";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import useLocalStorage from "@/utils/useLocalStorage";
import useMediaQuery from "@/utils/useMediaQuery";
import { DateTime } from "luxon";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useRouter } from "next13-progressbar";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ArrowRightIcon from "../_icons/arrowRightIcon";
import { TrashIcon } from "../_icons/trashIcon";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import { PrimaryButton } from "../button/primaryButton";
import { DrawerModal } from "../modals/modals";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import { ScheduledDateDropdown } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduledTimeDropdown } from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { CartMerchantInfo } from "./cartMerchantInfo";
import { SuccessBanner, UnavailableBanner } from "./cartStatusBanner";

import { CartDrawerLayout } from "./cartDrawerLayout";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { ROUTE_CHECKOUT } from "@/utils/constants";
import { Merchant } from "@/dto/merchant";
import {
  ReorderUnavailableItem,
  readAndClearReorderSession,
} from "@/utils/reorderValidation";
import { checkIsHomePage } from "@/utils/nativeBridgeUtils";
import { useAuth } from "@/app/providers/authProvider";

const extractHashFromUrl = (url: string) => {
  const urlObj = new URL(url);
  return urlObj.hash;
};

interface RetailCartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestClose: () => void;
  merchant: Merchant;
  onTriggerLoginModal: (value: boolean) => void;
  retailPickupTime?: any;
  setDeliveryAddressModalOpen: (value: boolean) => void;
  setIsRetailCartOpen: (value?: boolean | undefined) => void;
  setDeliveryAddressErrorModalOpen: (value: boolean) => void;
}

export const RetailCartDrawer = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  retailPickupTime,
  setDeliveryAddressModalOpen,
  setIsRetailCartOpen,
  setDeliveryAddressErrorModalOpen,
}: RetailCartDrawerProps) => {
  const hash =
    globalThis.window === undefined
      ? ""
      : extractHashFromUrl(globalThis?.location?.href);
  const pathName = usePathname();
  const { isLoggedIn } = useAuth();
  const t = useTranslations("RetailCartDrawer");
  const locale = useLocale();
  const isHomePage = checkIsHomePage(
    pathName,
    merchant.merchantMeta.selectedSetup === "Simple",
    merchant.isStandaloneGiftCardWebsite
  );

  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color ?? cloverColorTheme.Main.primaryColor;
  const isHybridMerchant = merchant.isHybrid;
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const deliveryService = merchant?.services.find(
    (s: Service) => s.type === ServiceType.DELIVERY
  );
  const now = new Date();
  const router = useRouter();
  const [cartItems] = useLocalStorage<RetailCartItem[]>(
    LocalStorageKey.RETAIL_CART,
    []
  );
  const [orderType, setOrderType] = useState<OrderType>((): OrderType => {
    const sessionTime = getFromSessionStorage<ScheduledTime>(
      SessionStorageKey.SCHEDULED_TIME,
      {} as ScheduledTime
    );
    const savedOrderType = sessionTime.orderType;

    const retailServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = retailServices.filter(
      ({ service }) => service?.enabled
    );

    if (
      savedOrderType &&
      enabledServices.some(
        (s: { type: OrderType; service: Service | undefined }) =>
          s.type === savedOrderType
      )
    ) {
      return savedOrderType;
    } else {
      return enabledServices.length > 0
        ? enabledServices[0]?.type
        : OrderType.PICKUP;
    }
  });
  const [showScheduledOrderForm, setShowScheduledOrderForm] =
    useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: t("today"),
    dateObject: new Date(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now),
  });
  // eslint-disable-next-line no-unused-vars
  const [giftCard, setGiftCard, removeGiftCard] =
    useLocalStorage<GiftCard | null>(LocalStorageKey.GIFT_CARD, null);
  /**
   * REFACTOR: For now moving away from hook and getting directly from local storage
   * Current hook need to be refactored as it returns the state with initial value first
   * and then state getting updated in useEffect returning later in render cycle causing issues
   * Need to refactor useLocalStorage hook to leverage useSyncExternalStore
   */
  const [orderEstimates, setOrderEstimates] = useState<Partial<OrderEstimates>>(
    () =>
      getFromSessionStorage<Partial<OrderEstimates>>(
        SessionStorageKey.ORDER_ESTIMATES,
        {}
      )
  );

  const deliveryAddress = getFromLocalStorage<Address>(
    LocalStorageKey.DELIVERY_ADDRESS,
    {} as Address
  );

  const businessHours = merchant?.businessHours?.hours;
  const allDaysEmpty =
    businessHours &&
    Object.values(businessHours).every((day: any) => day.length === 0);
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  let hasItemsInCart = cartItems.length > 0;
  const isMinimumMet = () => {
    const totalPrice =
      cartItems.reduce((total: number, i: any) => total + i.value.total, 0) /
      100;

    return merchant.deliveryMinimum / 100 <= totalPrice;
  };
  const onlyGiftCardInCart = !hasItemsInCart && giftCard;
  let checkoutDisabled =
    (cartItems.length == 0 && !giftCard) ||
    (!businessOpen && hasItemsInCart) ||
    !businessHours ||
    allDaysEmpty ||
    (retailPickupTime &&
      Object.keys(retailPickupTime).length === 0 &&
      hasItemsInCart) ||
    (orderType === OrderType.DELIVERY && !isMinimumMet());

  const itemsCount =
    cartItems.reduce(
      (adder: any, item: any) => adder + item.value?.quantity,
      0
    ) + (giftCard ? 1 : 0);

  const originalItemsCountRef = useRef(itemsCount);
  const { format } = useCurrency();

  const [reorderUnavailable, setReorderUnavailable] = useState<{
    needsReAdd: ReorderUnavailableItem[];
    hardUnavailable: ReorderUnavailableItem[];
  } | null>(null);
  const [reorderBannerDismissed, setReorderBannerDismissed] = useState(false);
  const [reorderAddedCount, setReorderAddedCount] = useState<number | null>(
    null
  );

  useEffect(() => {
    const isReorderHash = hash === "#cart" || hash === "#cart-unavailable";
    if (!isReorderHash) return;

    const isUnavailableHash = hash === "#cart-unavailable";
    const { unavailableItems, addedCount } =
      readAndClearReorderSession(isUnavailableHash);

    if (isUnavailableHash) {
      setReorderBannerDismissed(false);
      if (unavailableItems) setReorderUnavailable(unavailableItems);
    }
    if (addedCount !== null) setReorderAddedCount(addedCount);
  }, [hash]);

  useEffect(() => {
    if (merchant.isRetail) {
      saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
        orderType: orderType,
      });
    }

    const fetchEstimate = async () => {
      if (orderType === OrderType.DELIVERY) {
        if (!deliveryAddress?.address1) {
          setDeliveryAddressModalOpen(true);
          setIsRetailCartOpen(false);
        } else {
          try {
            const result = await getOrderEstimate(
              merchant.id,
              deliveryAddress,
              merchant.isRetail
                ? retailPickupTime?.pickupDateTimeMillis
                : undefined
            );
            saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, result);
            if (result) setOrderEstimates(result);
          } catch {
            setDeliveryAddressModalOpen(false);
            setDeliveryAddressErrorModalOpen(true);
            onRequestClose();
          }
        }
      }
    };
    fetchEstimate();
  }, [orderType]);

  useEffect(() => {
    if (isOpen && businessHours) {
      const pickupTimeData = {
        epoch: retailPickupTime?.pickupDateTimeMillis,
        type: PickupTimeType.SCHEDULED,
      };

      saveToSessionStorage(SessionStorageKey.PICKUP_TIME, pickupTimeData);
    }
  }, [isOpen, businessHours, merchant.leadTime, merchant.timezone]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (isMobileScreen) return;
      if (event.key === LocalStorageKey.DELIVERY_ORDER_TYPE) {
        setOrderType(OrderType.DELIVERY);
      } else if (event.key === LocalStorageKey.PICKUP_ORDER_TYPE) {
        const retailServices = [
          { type: OrderType.PICKUP, service: merchantService },
          { type: OrderType.DELIVERY, service: deliveryService },
        ];
        const enabledServices = retailServices.filter(
          ({ service }) => service?.enabled
        );
        if (enabledServices.length >= 1) {
          setOrderType(enabledServices[0]?.type);
        } else {
          setOrderType(OrderType.PICKUP);
        }
      }
    };
    globalThis.addEventListener("storage", handleStorageChange);
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileScreen]);

  useEffect(() => {
    if (isOpen && typeof globalThis !== "undefined") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    // To prevent stale banner messaging.
    if (originalItemsCountRef.current === 0 && itemsCount > 0) {
      originalItemsCountRef.current = itemsCount;
    }
  }, [itemsCount]);

  const removeFromCart = (event: React.MouseEvent, index: any) => {
    event.stopPropagation();
    const itemToRemove = cartItems[index];
    if (itemToRemove) {
      RetailCart.removeItem(itemToRemove.key);
    }
  };

  const handleBackToCart = () => {
    setShowScheduledOrderForm(false);
  };

  const handleConfirmScheduledOrder = () => {
    saveToSessionStorage<ScheduledTime>(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: scheduleDate,
      time: scheduleTime,
    });
    setShowScheduledOrderForm(false);
  };

  const handleContinueToCheckout = () => {
    if (orderType === OrderType.DELIVERY && !deliveryAddress?.address1) {
      setDeliveryAddressModalOpen(true);
      setIsRetailCartOpen(false);
    } else if (isLoggedIn) {
      router.push(ROUTE_CHECKOUT);
    } else {
      onTriggerLoginModal(true);
    }

    setIsRetailCartOpen(false);
  };

  const handleCartItemClick = (item: RetailCartItem) => {
    const selectedItem = item.value;
    if (merchant.isHybrid) {
      router.push(`/shop/${getItemUrl(selectedItem)}`);
    } else if (merchant.isRetail) {
      router.push(`/${getItemUrl(selectedItem)}`);
    }
    onRequestClose();
  };

  const pickupTimeDisplay =
    orderType === OrderType.DELIVERY
      ? formatDeliveryTime(
          merchant?.timezone,
          locale,
          t,
          orderEstimates,
          retailPickupTime
        )
      : formatPickupTime(
          retailPickupTime?.pickupDateTimeMillis,
          merchant.timezone,
          locale,
          t
        );

  const title =
    isHybridMerchant && !cartItems.length && !giftCard
      ? t("appointmentSummary")
      : t("yourCart", { count: itemsCount });

  const closeHandler = () => {
    Cart.removeHashFromURL(hash, pathName);
    onClose();
  };

  const requestCloseHandler = () => {
    setShowScheduledOrderForm(false);
    Cart.removeHashFromURL(hash, pathName);
    onRequestClose();
  };

  if (showScheduledOrderForm) {
    return (
      <DrawerModal
        isOpen={isOpen}
        onRequestClose={requestCloseHandler}
        contentClassName="flex flex-col h-full w-[100%] p-8"
        hideCloseButton={true}
      >
        <div className="pt-4 flex flex-row gap-2">
          <button
            onClick={() => {
              handleBackToCart();
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon />
          </button>
          <h2 className="text-xl font-bold">{t("scheduleYourOrder")}</h2>
        </div>
        <div className="py-4">
          <ScheduledDateDropdown
            scheduleDate={scheduleDate}
            setScheduleDate={setScheduleDate}
            scheduleDateRange={3}
            setScheduleTime={setScheduleTime}
            businessHours={businessHours}
            timezone={merchant.timezone}
          />
        </div>
        {businessHours && !allDaysEmpty && (
          <div className="py-4">
            <ScheduledTimeDropdown
              scheduleTime={scheduleTime}
              setScheduleTime={setScheduleTime}
              businessHours={businessHours}
              scheduleDate={scheduleDate}
              leadTime={merchant.leadTime}
              timezone={merchant.timezone}
              orderType={orderType}
            />
          </div>
        )}
        <div className="mt-auto pb-4 flex gap-4">
          <button
            className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
            onClick={handleConfirmScheduledOrder}
            disabled={scheduleTime.displayValue == ""}
          >
            {t("confirm")}
          </button>
        </div>
      </DrawerModal>
    );
  }

  return (
    <CartDrawerLayout
      isOpen={isOpen}
      isHomePage={isHomePage}
      onClose={closeHandler}
      onRequestClose={requestCloseHandler}
      title={title}
      className="md:w-[640px]"
      testId={applyDataTestId(pom.retailCart.cartDrawer)}
      closeCartLabel={t("closeCart")}
      hasItems={hasItemsInCart || !!onlyGiftCardInCart}
      headerContent={
        <>
          <CartMerchantInfo className="sm:hidden" merchant={merchant} />
          {hasItemsInCart && (
            <div className="pl-4 pr-2 pb-1 grid grid-cols-2 justify-between items-center gap-4 pt-6 sm:pt-8">
              <OrderTypeDropdown
                orderType={orderType}
                setOrderType={setOrderType}
                availableOrderTypes={merchant.services}
              />
              {businessHours && !allDaysEmpty && (
                <div className=" flex flex-col pb-[0px]">
                  <p className="text-[12px] text-[#727272] pb-0 tracking-wide">
                    {t("forWhen")}
                  </p>
                  {retailPickupTime && (
                    <p data-testid={applyDataTestId(pom.retailCart.pickupTime)}>
                      {pickupTimeDisplay}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {hash && hash === "#cart" && itemsCount > 0 && (
            <SuccessBanner
              count={
                reorderAddedCount ??
                (originalItemsCountRef.current || itemsCount)
              }
            />
          )}
          {hash && hash === "#cart-unavailable" && !reorderBannerDismissed && (
            <div className="pt-4">
              <UnavailableBanner
                count={
                  reorderAddedCount ??
                  (originalItemsCountRef.current || itemsCount)
                }
                needsReAdd={reorderUnavailable?.needsReAdd}
                hardUnavailable={reorderUnavailable?.hardUnavailable}
                onDismiss={() => setReorderBannerDismissed(true)}
              />
            </div>
          )}
          {!businessOpen && hasItemsInCart && giftCard && (
            <div className="mx-4">
              <GiftCardCartOrdersBanner />
            </div>
          )}
        </>
      }
      footerContent={
        <PrimaryButton
          style={{
            backgroundColor: checkoutDisabled ? "#C7C7C7" : merchantColor,
          }}
          className="bg-black px-[18px] sm:px-0 text-white font-bold py-2 rounded-none sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center tracking-wide"
          onClick={handleContinueToCheckout}
          disabled={checkoutDisabled}
          data-testid={applyDataTestId(pom.drawer.continueToCheckoutCartButton)}
          aria-labelledby="button-text"
        >
          <p
            id="button-text"
            aria-hidden="true"
            className="text-xl font-medium"
          >
            {t("continueToCheckout")}
          </p>
          <ArrowRightIcon
            className="sm:hidden"
            color="white"
            width={24}
            height={24.7}
          />
        </PrimaryButton>
      }
      ariaLabel={t("yourCartLabel")}
    >
      {orderType === OrderType.DELIVERY && hasItemsInCart && (
        <DeliveryEstimateBanner
          merchant={merchant}
          onAddMore={setIsRetailCartOpen}
        />
      )}
      {cartItems.map((item: any, index: any) => (
        <div
          key={`${item.key}-${index}`}
          className="w-full flex flex-row justify-between py-4 border-b"
        >
          <button
            className="flex w-full flex-col justify-between items-center cursor-pointer"
            onClick={() => handleCartItemClick(item)}
            data-testid={applyDataTestId(pom.retailCart.items)}
            aria-label={t("editItem", { itemName: item.value?.name })}
          >
            <div
              className="w-full flex flex-col justify-between items-start"
              aria-hidden="true"
            >
              <p className="text-left text-lg text-[#373737] max-w-[310px]">
                {item.value?.name}{" "}
                {item.value?.quantity > 1 && `× ` + item.value?.quantity}
              </p>
              {item.value?.isAgeRestricted && (
                <AgeRestricted text={t("idRequired")} />
              )}
              <div className="max-w-[80%]">
                {item.value?.modifiers?.length > 0 && (
                  <p className="text-[15px] text-[#727272] text-left">
                    {modifierDisplay(item.value?.modifiers)}
                  </p>
                )}
                <div className="w-full">
                  <p className="text-[15px] text-[#727272] max-w-[310px] break-words text-left">
                    {item.value?.specialInstructions}
                  </p>
                </div>
              </div>
            </div>
          </button>
          <div className="flex flex-col justify-start items-end">
            <p className="text-[16px] font-medium mt-0.5">
              {format(item.value?.total)}
            </p>
            <button
              onClick={(event: React.MouseEvent) => {
                removeFromCart(event, index);
                setReorderBannerDismissed(true);
              }}
              className="mt-2"
              aria-label={t("removeItem", { itemName: item.value?.name })}
            >
              <TrashIcon width={14} height={14} />
            </button>
          </div>
        </div>
      ))}
      {giftCard && (
        <GiftCardCartItem
          giftCard={giftCard}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            removeGiftCard();
          }}
          onRequestClose={onRequestClose}
          showBottomBorder={true}
        />
      )}
    </CartDrawerLayout>
  );
};

const formatPickupTime = (
  pickupTimeData: number,
  timezone: string,
  locale: string,
  t: TFunction
) => {
  if (!pickupTimeData) return t("scheduledTimeNotAvailable");

  const date = new Date(pickupTimeData);

  const formattedDate = date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });

  const formattedTime = date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });

  return t("pickupTime", {
    date: formattedDate,
    time: formattedTime,
    timeZone: getTimezoneAbbreviation(timezone, locale),
  });
};

const formatDeliveryTime = (
  timezone: string,
  locale: string,
  t: TFunction,
  estimates: Partial<OrderEstimates>,
  retailPickupTime?: any
) => {
  const { deliveryTime, pickupTime: deliveryPickupTime } = estimates;

  if (!deliveryTime || !deliveryPickupTime) {
    return formatPickupTime(
      retailPickupTime?.pickupDateTimeMillis,
      timezone,
      locale,
      t
    );
  }

  const createDateTime = (ms: number) =>
    DateTime.fromMillis(ms || 0)
      .setZone(timezone)
      .setLocale(locale);

  const pickupDt = createDateTime(deliveryPickupTime || 0);
  const deliveryDt = createDateTime(deliveryTime || 0);

  const deliveryPickupTimeDateText = pickupDt.toLocaleString(
    DateTime.TIME_SIMPLE
  );
  const deliveryTimeDateText = deliveryDt.toLocaleString(DateTime.TIME_SIMPLE);

  const timeZoneAbbreviation = getTimezoneAbbreviation(timezone, locale);
  const now = DateTime.now().setZone(timezone);

  let dayLabel;
  if (deliveryDt.hasSame(now, "day")) {
    dayLabel = t("today");
  } else if (deliveryDt.hasSame(now.plus({ days: 1 }), "day")) {
    dayLabel = `${t("tomorrow")} (${deliveryDt
      .setLocale(locale)
      .toFormat("MMM d")})`;
  } else {
    dayLabel = deliveryDt.setLocale(locale).toFormat("MMM d");
  }

  return t("deliveryTime", {
    day: dayLabel,
    fromDate: deliveryPickupTimeDateText,
    toDate: deliveryTimeDateText,
    timeZone: timeZoneAbbreviation,
  });
};
